/**
 * VaultBacklinksPane — incoming wikilinks for the open note.
 *
 * Stacks below the embedded graph (Q28 said backlinks may fold into the
 * graph column when split is active; we keep it always-visible underneath
 * so the relationship is discoverable even when split is closed).
 */

import { useMemo } from 'react';
import { testIds } from '../../lib/testIds';
import type { ParsedNote } from '../../lib/vault/parse';
import { buildVaultLinks } from '../../lib/vault/parse';

export interface VaultBacklinksPaneProps {
  notes: ParsedNote[];
  activePath: string | null;
  onOpen: (path: string, modifier: 'pane-a' | 'pane-b') => void;
}

export function VaultBacklinksPane({
  notes,
  activePath,
  onOpen,
}: VaultBacklinksPaneProps) {
  const backlinks = useMemo(() => {
    if (!activePath) return [] as { fromPath: string; kind: string }[];
    return buildVaultLinks(notes)
      .filter((r) => r.toNotePath === activePath)
      .map((r) => ({ fromPath: r.fromNotePath, kind: r.kind }));
  }, [notes, activePath]);

  return (
    <div
      data-testid={testIds.obsidianVault.backlinksPane}
      style={{
        background: '#f0f0f0',
        borderTop: '1px solid #808080',
        padding: 6,
        minHeight: 0,
        maxHeight: '40%',
        overflowY: 'auto',
      }}
    >
      <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
        Backlinks ({backlinks.length})
      </div>
      {activePath ? (
        backlinks.length === 0 ? (
          <div style={{ color: '#606060', fontStyle: 'italic' }}>
            Nothing links here yet.
          </div>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {backlinks.map((b) => (
              <li key={b.fromPath}>
                <span
                  className="vault-wikilink"
                  onClick={(e) =>
                    onOpen(b.fromPath, e.metaKey || e.ctrlKey ? 'pane-b' : 'pane-a')
                  }
                >
                  {b.fromPath}
                </span>
                <span style={{ color: '#808080', marginLeft: 6, fontSize: 11 }}>
                  ({b.kind})
                </span>
              </li>
            ))}
          </ul>
        )
      ) : (
        <div style={{ color: '#606060', fontStyle: 'italic' }}>
          Open a note to see its backlinks.
        </div>
      )}
    </div>
  );
}
